import ScratchJr from '../../../ScratchJr';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import GoalMarker from './GoalMarker';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import Palette from '../../../ui/Palette';
import Thumbs from '../../../ui/Thumbs';
import {gn} from '../../../../utils/lib';
import {
    analyzeMoonwalkScripts,
    detectMoonwalkChange,
    serializeTopBlocks
} from './MoonwalkMachineAnalyzer';
import {evaluateSuccessCriteria} from '../../../../../../web/ai/activity-success';

const DEFAULT_PHASE = 'INTRO';
const PHASES = Object.freeze({
    INTRO: 'INTRO',
    PHASE_1_STACK: 'PHASE_1_STACK',
    LOOP_INTRO: 'LOOP_INTRO',
    PHASE_2_LOOP: 'PHASE_2_LOOP',
    PHASE_3_UNLOCK: 'PHASE_3_UNLOCK',
    PHASE_3_BUILD: 'PHASE_3_BUILD',
    CELEBRATION: 'CELEBRATION',
    BONUS: 'BONUS',
    COMPLETE: 'COMPLETE'
});

const PHASE_RANKS = Object.freeze({
    INTRO: 0,
    PHASE_1_STACK: 1,
    LOOP_INTRO: 2,
    PHASE_2_LOOP: 3,
    PHASE_3_UNLOCK: 4,
    PHASE_3_BUILD: 5,
    CELEBRATION: 6,
    BONUS: 7,
    COMPLETE: 8
});

const DEFAULT_VOICE_OVER = Object.freeze({
    'VO-01': "Hi! I'm Blooks the astronaut! I just landed on the moon! Help me dance! See the green flag? Drag it down to start.",
    'VO-02': 'Find a blue arrow block. Drag it next to the green flag. Then press the flag to see me walk!',
    'VO-03': "You stacked lots of move blocks! Nice work! But what if one block could do all of that?",
    'VO-04': 'See this orange block? It repeats things! Put one of your blocks inside it.',
    'VO-05': "One block made me do it lots of times! That is called a loop!",
    'VO-06': "More repeats, more dancing! You control how long the dance goes!",
    'VO-07': 'Now the loop has two things inside! It does everything inside it, over and over!',
    'VO-08': "That is a real dance! Want to make it even fancier?",
    'VO-09': 'New blocks! Spins, growing, shrinking, and speed! Add them to your loop!',
    'VO-10': 'What else could you add to the dance?',
    'VO-11': 'The dance looks different! You put something new in the loop!',
    'VO-12': 'Whoa, that was longer! What did you change?',
    'VO-13': 'Some blocks are outside the loop. Can you move them inside so they repeat too?',
    'VO-14': "Nice! Same dance, less blocks. That is called refactoring!",
    'VO-15': "The moonwalk! You built a whole dance with one loop! You are a loop master!",
    'VO-16': 'Want to dance forever? Try the red forever block! The purple block lets me talk!',
    'VO-17': "Mission complete! A loop makes blocks repeat. You built your program step by step. That is what real programmers do!",
    'VO-18': 'Those blocks are outside the loop. Put them inside so they repeat too!',
    'VO-19': 'The dance got shorter. What happened?',
    'VO-20': 'Faster! Blooks is really moving now!',
    'VO-21': 'The loop says zero. Zero means do it zero times! Try a bigger number.',
    'VO-22': 'That was a long dance! Change the number to make it shorter or longer.',
    'VO-23': 'The dance goes forever! Tap the red stop to stop it.'
});

const DEFAULT_AVAILABLE_BLOCKS = Object.freeze({
    PHASE_1_STACK: ['onflag', 'forward', 'back', 'wait', 'endstack'],
    LOOP_INTRO: ['onflag', 'forward', 'back', 'up', 'down', 'hop', 'playsnd', 'wait', 'repeat', 'endstack'],
    PHASE_2_LOOP: ['onflag', 'forward', 'back', 'up', 'down', 'hop', 'playsnd', 'wait', 'repeat', 'endstack'],
    PHASE_3_BUILD: ['onflag', 'forward', 'back', 'up', 'down', 'hop', 'right', 'left', 'grow', 'shrink', 'setspeed', 'playsnd', 'wait', 'repeat', 'endstack'],
    BONUS: ['onflag', 'forward', 'back', 'up', 'down', 'hop', 'right', 'left', 'grow', 'shrink', 'say', 'playsnd', 'playusersnd', 'setspeed', 'wait', 'repeat', 'endstack', 'forever']
});

const DEFAULT_IDLE_SCAFFOLDING = Object.freeze({
    PHASE_1_STACK: [25000, 45000, 65000, 85000],
    LOOP_INTRO: [25000, 45000, 65000, 85000],
    PHASE_2_LOOP: [25000, 45000, 65000, 85000],
    PHASE_3_BUILD: [25000, 45000, 65000, 85000],
    BONUS: []
});

export default class MoonwalkMachineFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._overlay = null;
        this._celebrationLayer = null;
        this._footprints = null;
        this._goalMarker = null;
        this._doneBtn = null;
        this._ghostHand = null;
        this._loopPulseTimer = null;
        this._scriptMonitor = null;
        this._persistTimer = null;
        this._replaySpriteEl = null;
        this._completionOverlay = null;
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._longDanceTimeout = null;
        this._foreverTimeout = null;
        this._replayListenerAttached = false;
        this._onPointerDown = this._handleInteraction.bind(this);
        this._onPointerUpReplay = this._replayCurrentNarration.bind(this);
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
        this._phase = DEFAULT_PHASE;
        this._config = this._buildConfig(activityData);
        this._paletteRestrictor = null;
        this._analysis = analyzeMoonwalkScripts([]);
        this._lastAnalysis = this._analysis;
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        this._sessionStartedAt = Date.now();
        this._flagTapCount = 0;
        this._phase3RunCount = 0;
        this._parameterChanges = 0;
        this._reflectionPromptsFired = 0;
        this._duplicateBlocksRemoved = 0;
        this._maxLoopDepth = 0;
        this._lastMeaningfulChange = null;
        this._scriptEditedSinceRun = false;
        this._runsWithEdits = 0;
        this._loopDiscovered = false;
        this._repeatParameterChanged = false;
        this._multiActionLoop = false;
        this._refactorPromptShown = false;
        this._refactorCelebrated = false;
        this._foreverPromptShown = false;
        this._completionSaved = false;
        this._phaseIntroStarted = false;
        this._speechVoice = null;
        this._speechVoiceScore = 0;
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false,
            outcome_6: false
        };
    }

    async init () {
        this._renderChrome();
        this._attachListeners();
        this._applySpriteRoles();
        this._forcePlayerSelection();
        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._initVoiceSelection();
        await this._waitForPreferredVoice();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved) {
            this._hydrateFromProgress(saved);
        }

        this._updateAnalysis();
        const resumePhase = this._getResumePhase(saved && saved.currentState ? saved.currentState : null);

        if (resumePhase) {
            this._phase = resumePhase;
            this._syncVisualState({skipReset: true});
            this._startScriptMonitor();
            this._startIdleScaffolding();
            this._speak('VO-10', this._buildPhasePrompt(this._phase, true));
            if (saved && resumePhase !== this._normalizeResumePhase(saved.currentState)) {
                await this._persistProgress({currentState: resumePhase});
            }
            return;
        }

        this._setPhase(PHASES.INTRO, {skipPersist: true, skipReset: true});
        this._syncVisualState({skipReset: true});
        this._startScriptMonitor();
        this._playIntroSequence();
        await this._persistProgress({
            status: 'in_progress',
            currentState: PHASES.INTRO
        });
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            return;
        }

        if (newState === 'RUNNING') {
            this._clearIdleTimers();
            this._startLoopPulse();
            this._startForeverPromptTimer();
            return;
        }

        if (newState === 'VALIDATING') {
            this._stopLoopPulse();
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._stopLoopPulse();
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return this._analysis.totalBlocks || 0;
    }

    evaluate () {
        const simState = this._buildSimState();
        const shared = evaluateSuccessCriteria(this._activityData, simState);
        return Object.assign({}, shared, {
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase,
            flagTapCount: this._flagTapCount,
            maxLoopDepth: this._maxLoopDepth
        });
    }

    _buildSimState () {
        return {
            phaseFlags: {
                completed: this._phase === PHASES.COMPLETE || this._completionSaved,
                storyPhase: this._phase,
                flagTapCount: this._flagTapCount,
                maxLoopDepth: this._maxLoopDepth
            }
        };
    }

    showHint () {
        this._replayCurrentNarration();
    }

    captureRunStartPosition () {}

    shouldResetSpritesBeforeRun () {
        return true;
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }

        this._clearLongDanceTimeout();
        this._clearForeverTimeout();
        this._clearPlayerScript();
        this._sessionStartedAt = Date.now();
        this._idleTimeMs = 0;
        this._idlePausedAt = null;
        this._lastInteractionAt = Date.now();
        this._phaseIntroStarted = false;
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._flagTapCount = 0;
        this._phase3RunCount = 0;
        this._parameterChanges = 0;
        this._reflectionPromptsFired = 0;
        this._duplicateBlocksRemoved = 0;
        this._maxLoopDepth = 0;
        this._runsWithEdits = 0;
        this._analysis = analyzeMoonwalkScripts([]);
        this._lastAnalysis = this._analysis;
        this._loopDiscovered = false;
        this._repeatParameterChanged = false;
        this._multiActionLoop = false;
        this._refactorPromptShown = false;
        this._refactorCelebrated = false;
        this._foreverPromptShown = false;
        this._completionSaved = false;
        this._lastMeaningfulChange = null;
        this._scriptEditedSinceRun = false;
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false,
            outcome_6: false
        };
        this._setPhase(PHASES.PHASE_1_STACK, {skipReset: false});
        this._startScriptMonitor();
        this._startIdleScaffolding();
        this._speak('VO-02', this._buildPhasePrompt(PHASES.PHASE_1_STACK));
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
        return true;
    }

    destroy () {
        this._clearIdleTimers();
        this._clearPersistTimer();
        this._clearPhaseTimeout();
        this._clearLongDanceTimeout();
        this._clearForeverTimeout();
        this._stopScriptMonitor();
        this._stopLoopPulse();
        this._dismissCompletionModal();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
        if (this._goalMarker) {
            this._goalMarker.destroy();
            this._goalMarker = null;
        }
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
        }
        if (this._overlay && this._overlay.parentNode) {
            this._overlay.parentNode.removeChild(this._overlay);
        }
        this._removeReplayListener();
        this._container.removeEventListener('pointerdown', this._onPointerDown, true);
    }

    _buildConfig (activityData) {
        const machine = activityData.moonwalkMachine || {};
        const storyWalk = activityData.storyWalk || {};
        const goal = activityData.successCriteria || {};
        return {
            playerIndex: typeof machine.playerIndex === 'number' ? machine.playerIndex : 0,
            goalIndex: typeof machine.goalIndex === 'number' ? machine.goalIndex : 1,
            introDelayMs: machine.introDelayMs || 900,
            voiceOver: {
                ...DEFAULT_VOICE_OVER,
                ...(storyWalk.voiceOver || {}),
                ...(machine.voiceOver || {})
            },
            availableBlocks: {
                ...DEFAULT_AVAILABLE_BLOCKS,
                ...(machine.availableBlocks || {})
            },
            idleScaffolding: {
                ...DEFAULT_IDLE_SCAFFOLDING,
                ...(machine.idleScaffolding || {})
            },
            voiceConfidenceThreshold: machine.voiceConfidenceThreshold || storyWalk.voiceConfidenceThreshold || 90,
            goalX: typeof goal.goalX === 'number' ? goal.goalX : 404,
            goalY: typeof goal.goalY === 'number' ? goal.goalY : 218,
            goalTolerance: typeof goal.tolerance === 'number' ? goal.tolerance : 30,
            footprints: Array.isArray(machine.footprints) ? machine.footprints : [
                {x: 110, y: 262},
                {x: 155, y: 258},
                {x: 202, y: 262},
                {x: 248, y: 258},
                {x: 296, y: 262},
                {x: 344, y: 258}
            ]
        };
    }

    _renderChrome () {
        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-moonwalk-layer';

        this._footprints = document.createElement('div');
        this._footprints.className = 'activity-moonwalk-footprints';
        this._config.footprints.forEach((footprint, index) => {
            const step = document.createElement('span');
            step.className = 'activity-moonwalk-footprint';
            if (index % 2 === 1) {
                step.classList.add('is-right');
            }
            step.style.left = `${footprint.x}px`;
            step.style.top = `${footprint.y}px`;
            this._footprints.appendChild(step);
        });
        this._overlay.appendChild(this._footprints);

        this._celebrationLayer = document.createElement('div');
        this._celebrationLayer.className = 'activity-moonwalk-celebration';
        this._overlay.appendChild(this._celebrationLayer);

        this._stage.appendChild(this._overlay);

        this._goalMarker = new GoalMarker(
            this._stage,
            this._config.goalX,
            this._config.goalY,
            this._config.goalTolerance
        );
        this._goalMarker.show();
    }

    _attachListeners () {
        this._container.addEventListener('pointerdown', this._onPointerDown, true);
        this._attachReplayListener();
    }

    _attachReplayListener () {
        const playerEl = this._getPlayerSpriteElement();
        if (!playerEl) {
            return;
        }

        if (this._replayListenerAttached && this._replaySpriteEl === playerEl) {
            return;
        }

        this._removeReplayListener();
        playerEl.addEventListener('pointerup', this._onPointerUpReplay);
        this._replaySpriteEl = playerEl;
        this._replayListenerAttached = true;
    }

    _removeReplayListener () {
        const playerEl = this._replaySpriteEl;
        if (playerEl) {
            playerEl.removeEventListener('pointerup', this._onPointerUpReplay);
        }
        this._replaySpriteEl = null;
        this._replayListenerAttached = false;
    }

    _handleInteraction () {
        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }
        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._startIdleScaffolding();
        const previousSignature = this._analysis ? this._analysis.signature : '';
        const nextAnalysis = this._updateAnalysis();
        if (nextAnalysis.signature !== previousSignature) {
            this._schedulePersistProgress();
        }
    }

    _hydrateFromProgress (saved) {
        this._sessionStartedAt = typeof saved.sessionStartedAt === 'number' ? saved.sessionStartedAt : Date.now();
        this._idleTimeMs = typeof saved.idleTimeMs === 'number' ? saved.idleTimeMs : 0;
        this._flagTapCount = Number(saved.flagTapCount || 0);
        this._phase3RunCount = Number(saved.phase3RunCount || 0);
        this._parameterChanges = Number(saved.parameterChanges || 0);
        this._reflectionPromptsFired = Number(saved.reflectionPromptsFired || 0);
        this._duplicateBlocksRemoved = Number(saved.duplicateBlocksRemoved || 0);
        this._maxLoopDepth = Number(saved.maxLoopDepth || 0);
        this._loopDiscovered = Boolean(saved.loopDiscovered);
        this._repeatParameterChanged = Boolean(saved.repeatParameterChanged);
        this._multiActionLoop = Boolean(saved.multiActionLoop);
        this._completionSaved = saved.status === 'completed';
        if (saved.outcomeFlags) {
            this._outcomeFlags = {...this._outcomeFlags, ...saved.outcomeFlags};
        }
    }

    _getPhaseRank (phase) {
        return Object.prototype.hasOwnProperty.call(PHASE_RANKS, phase)
            ? PHASE_RANKS[phase]
            : PHASE_RANKS.INTRO;
    }

    _normalizeResumePhase (savedState) {
        if (!savedState || !PHASES[savedState]) {
            return null;
        }

        if (savedState === PHASES.CELEBRATION) {
            return PHASES.BONUS;
        }

        if (savedState === PHASES.INTRO || savedState === PHASES.LOOP_INTRO) {
            return PHASES.PHASE_2_LOOP;
        }

        return savedState;
    }

    _getResumePhase (savedState, analysis = this._analysis) {
        const requestedPhase = this._normalizeResumePhase(savedState);
        if (!requestedPhase) {
            return null;
        }

        if (this._completionSaved || requestedPhase === PHASES.COMPLETE) {
            return requestedPhase;
        }

        const current = analysis || analyzeMoonwalkScripts([]);
        let derivedPhase = PHASES.PHASE_1_STACK;

        if (!current.hasOnflag) {
            derivedPhase = PHASES.PHASE_1_STACK;
        } else if (current.hasForever || current.hasSay) {
            derivedPhase = PHASES.BONUS;
        } else if (
            current.hasRepeatWithInner &&
            (current.insideDistinctCount >= 2 || current.hasSpeed || current.hasGrowOrShrink)
        ) {
            derivedPhase = PHASES.PHASE_3_BUILD;
        } else if (
            current.hasRepeat ||
            current.hasRepeatWithInner ||
            current.maxDuplicateOutsideCount >= 3 ||
            current.hasHighMoveParameter
        ) {
            derivedPhase = PHASES.PHASE_2_LOOP;
        }

        return this._getPhaseRank(derivedPhase) < this._getPhaseRank(requestedPhase)
            ? derivedPhase
            : requestedPhase;
    }

    _playIntroSequence () {
        if (this._phaseIntroStarted) {
            return;
        }
        this._phaseIntroStarted = true;
        this._clearPhaseTimeout();
        this._phaseTimeout = setTimeout(() => {
            this._phaseTimeout = null;
            this._showPlayerBubble('Moonwalk?', 2800);
            this._speak('VO-01', null, {
                onend: () => {
                    this._setPhase(PHASES.PHASE_1_STACK, {skipReset: false});
                    if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
                        this._stateMachine.transition('BUILDING');
                    }
                    this._speak('VO-02', this._buildPhasePrompt(PHASES.PHASE_1_STACK));
                }
            });
        }, this._config.introDelayMs);
    }

    _setPhase (phase, opts = {}) {
        this._clearPhaseTimeout();
        this._phase = phase;
        this._syncVisualState({skipReset: Boolean(opts.skipReset)});
        this._startIdleScaffolding();
        if (!opts.skipPersist) {
            this._persistProgress();
        }
    }

    _syncVisualState (opts = {}) {
        if (this._paletteRestrictor) {
            this._paletteRestrictor.updateAllowed(this._getAllowedBlocksForPhase());
        }

        if (!opts.skipReset) {
            this._removeReplayListener();
            ScratchJr.stopStrips();
            ScratchJr.resetSprites();
        }

        this._applySpriteRoles();
        this._forcePlayerSelection();
        this._attachReplayListener();
        this._selectPaletteCategory(this._getPreferredPaletteCategory());

        if (this._goalMarker) {
            if (this._phase === PHASES.PHASE_1_STACK || this._phase === PHASES.LOOP_INTRO) {
                this._goalMarker.show();
            } else {
                this._goalMarker.hide();
            }
        }

        if (this._footprints) {
            this._footprints.classList.toggle('is-hidden', this._phase !== PHASES.PHASE_1_STACK);
            this._footprints.classList.remove('is-faded');
        }

        if (this._phase === PHASES.BONUS || this._phase === PHASES.COMPLETE) {
            this._showDoneButton();
        } else {
            this._hideDoneButton();
        }

        this._updateAnalysis();
    }

    _getAllowedBlocksForPhase () {
        if (this._phase === PHASES.INTRO) {
            return ['onflag'];
        }

        if (this._phase === PHASES.PHASE_3_UNLOCK) {
            return this._config.availableBlocks.PHASE_3_BUILD || [];
        }

        return this._config.availableBlocks[this._phase] ||
            this._config.availableBlocks.PHASE_2_LOOP ||
            [];
    }

    _getPreferredPaletteCategory () {
        if (this._phase === PHASES.PHASE_1_STACK && !this._analysis.hasOnflag) {
            return 0;
        }

        if (this._phase === PHASES.PHASE_1_STACK || this._phase === PHASES.LOOP_INTRO) {
            return this._analysis.hasOnflag ? 1 : 0;
        }

        if ((this._phase === PHASES.PHASE_2_LOOP || this._phase === PHASES.PHASE_3_UNLOCK) && !this._analysis.hasRepeatWithInner) {
            return 4;
        }

        if (this._phase === PHASES.BONUS && this._analysis.hasForever) {
            return 5;
        }

        if (this._phase === PHASES.BONUS) {
            return 2;
        }

        return this._analysis.hasRepeatWithInner ? 1 : 4;
    }

    _startScriptMonitor () {
        this._stopScriptMonitor();
        this._scriptMonitor = setInterval(() => {
            this._updateAnalysis();
        }, 300);
    }

    _stopScriptMonitor () {
        if (this._scriptMonitor) {
            clearInterval(this._scriptMonitor);
            this._scriptMonitor = null;
        }
    }

    _updateAnalysis () {
        const serialized = this._getSerializedScripts();
        const nextAnalysis = analyzeMoonwalkScripts(serialized);
        const previousAnalysis = this._analysis || analyzeMoonwalkScripts([]);
        const changed = previousAnalysis.signature !== nextAnalysis.signature;
        const change = detectMoonwalkChange(previousAnalysis, nextAnalysis);

        this._lastAnalysis = previousAnalysis;
        this._analysis = nextAnalysis;
        this._maxLoopDepth = Math.max(this._maxLoopDepth, nextAnalysis.insideDistinctCount || 0);

        if (changed) {
            this._scriptEditedSinceRun = true;
            this._clearLongDanceTimeout();
            if (change && change.kind === 'repeat-parameter') {
                this._parameterChanges++;
                this._repeatParameterChanged = true;
                this._outcomeFlags.outcome_2 = true;
            }
            if (
                previousAnalysis.maxDuplicateOutsideCount > 0 &&
                nextAnalysis.maxDuplicateOutsideCount < previousAnalysis.maxDuplicateOutsideCount
            ) {
                this._duplicateBlocksRemoved += previousAnalysis.maxDuplicateOutsideCount - nextAnalysis.maxDuplicateOutsideCount;
            }
            this._lastMeaningfulChange = change;
            if (this._phase === PHASES.PHASE_2_LOOP || this._phase === PHASES.PHASE_3_BUILD) {
                this._handleOutsideLoopCoaching();
            }
        }

        return nextAnalysis;
    }

    _getSerializedScripts () {
        const player = this._getPlayerSpriteOwner();
        const scriptsDiv = player ? gn(`${player.id}_scripts`) : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.gettopblocks !== 'function') {
            return [];
        }
        return serializeTopBlocks(scriptsDiv.owner.gettopblocks());
    }

    _validateRun () {
        this._flagTapCount++;
        const analysis = this._updateAnalysis();

        if (analysis.repeatArg === 0 && analysis.hasRepeat) {
            this._showPlayerBubble('Zero?!', 2200);
            this._speak('VO-21');
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (analysis.repeatArg >= 20 && analysis.hasRepeat) {
            this._scheduleLongDancePrompt();
        } else {
            this._clearLongDanceTimeout();
        }

        if (analysis.hasForever) {
            this._foreverPromptShown = false;
        }

        if (this._scriptEditedSinceRun) {
            this._runsWithEdits++;
            this._scriptEditedSinceRun = false;
        }
        if (this._runsWithEdits >= 5) {
            this._outcomeFlags.outcome_4 = true;
        }

        switch (this._phase) {
        case PHASES.PHASE_1_STACK:
            this._validatePhaseOne(analysis);
            return;
        case PHASES.LOOP_INTRO:
        case PHASES.PHASE_2_LOOP:
            this._validatePhaseTwo(analysis);
            return;
        case PHASES.PHASE_3_UNLOCK:
            this._advanceToPhaseThreeBuild();
            return;
        case PHASES.PHASE_3_BUILD:
            this._validatePhaseThree(analysis);
            return;
        case PHASES.BONUS:
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        default:
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
        }
    }

    _validatePhaseOne (analysis) {
        const hasStackedWalk = analysis.maxDuplicateOutsideCount >= 3;
        const hasHighMoveParameter = analysis.hasHighMoveParameter;
        if (analysis.hasOnflag && (hasStackedWalk || hasHighMoveParameter)) {
            this._showPlayerBubble("I'm walking!", 2600);
            this._advanceToLoopIntro();
            return;
        }

        if (!analysis.hasOnflag) {
            this._showPlayerBubble('Need a flag!', 2200);
            this._speak('VO-02', 'Start with the green flag. Then add a blue move block.');
        } else if (analysis.totalBlocks <= 2) {
            this._showPlayerBubble('More blocks!', 2200);
            this._speak('VO-02', 'Add more blue move blocks to make a longer walk.');
        } else {
            this._showPlayerBubble('Almost!', 2200);
            this._speak('VO-03', 'Stack a few more blue move blocks.');
        }
        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _validatePhaseTwo (analysis) {
        if (!analysis.hasRepeatWithInner) {
            this._speak('VO-04');
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        let narration = null;
        const discoveredLoopNow = !this._loopDiscovered;
        const changedRepeatNow =
            this._lastMeaningfulChange &&
            this._lastMeaningfulChange.kind === 'repeat-parameter';
        const unlockedMultiActionNow = !this._multiActionLoop && analysis.insideDistinctCount >= 2;

        if (discoveredLoopNow) {
            this._loopDiscovered = true;
            this._outcomeFlags.outcome_1 = true;
            this._showPlayerBubble('A loop!', 2600);
            narration = !unlockedMultiActionNow && !this._repeatParameterChanged
                ? 'You made a loop! Now tap the number on the orange block and run it again.'
                : 'VO-05';
        }

        if (changedRepeatNow) {
            this._repeatParameterChanged = true;
            this._outcomeFlags.outcome_2 = true;
            if (!discoveredLoopNow) {
                this._showPlayerBubble('More dancing!', 2400);
            }
            narration = narration || (
                this._multiActionLoop
                    ? 'VO-06'
                    : 'Nice! You changed the number. Now add one more block inside the loop.'
            );
        }

        if (unlockedMultiActionNow) {
            this._multiActionLoop = true;
            this._outcomeFlags.outcome_3 = true;
            if (!discoveredLoopNow && !changedRepeatNow) {
                this._showPlayerBubble('Fancy moves!', 2600);
            }
            narration = (
                discoveredLoopNow || changedRepeatNow
                    ? 'VO-07'
                    : narration || (
                this._repeatParameterChanged
                    ? 'VO-07'
                    : 'Nice! Now tap the number on the orange block and change it.'
                )
            );
        }

        if (narration) {
            if (!discoveredLoopNow && !changedRepeatNow && !unlockedMultiActionNow) {
                this._showPlayerBubble(this._buildPhaseBubble(PHASES.PHASE_2_LOOP), 2600);
            }
            if (narration.indexOf(' ') > -1 && narration.indexOf('VO-') !== 0) {
                this._speak('VO-10', narration);
            } else {
                this._speak(narration);
            }
        }

        if (this._repeatParameterChanged && this._multiActionLoop) {
            this._advanceToPhaseThreeUnlock();
            return;
        }

        if (narration) {
            this._setPhase(PHASES.PHASE_2_LOOP, {skipReset: true});
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (analysis.hasOutsideLoopContent) {
            this._showPlayerBubble('Move them in!', 2200);
            this._speak('VO-18', 'Drag the extra blocks into the orange block so they repeat too.');
        } else if (!this._repeatParameterChanged) {
            this._showPlayerBubble('Try the number!', 2200);
            this._speak('VO-12', 'Try changing the number on the orange block, then run it again.');
        } else if (!this._multiActionLoop) {
            this._showPlayerBubble('One more block!', 2200);
            this._speak('VO-07', 'Add one more block inside the loop.');
        }

        this._setPhase(PHASES.PHASE_2_LOOP, {skipReset: true});
        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _validatePhaseThree (analysis) {
        this._phase3RunCount++;
        if (this._shouldCelebrate(analysis)) {
            this._advanceToCelebration();
            return;
        }

        if (!this._refactorPromptShown && analysis.hasOutsideLoopContent) {
            this._refactorPromptShown = true;
            this._showPlayerBubble('Hmm...', 2200);
            this._speak('VO-13');
        } else if (
            this._refactorPromptShown &&
            !this._refactorCelebrated &&
            this._lastAnalysis &&
            this._lastAnalysis.hasOutsideLoopContent &&
            !analysis.hasOutsideLoopContent
        ) {
            this._refactorCelebrated = true;
            this._outcomeFlags.outcome_5 = true;
            this._showPlayerBubble('Clean code!', 2600);
            this._speak('VO-14');
        } else {
            this._maybeSpeakReflectionPrompt();
        }

        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _shouldCelebrate (analysis) {
        return analysis.hasRepeatWithInner &&
            analysis.insideDistinctCount >= 3 &&
            this._phase3RunCount >= 3;
    }

    _advanceToLoopIntro () {
        this._setPhase(PHASES.LOOP_INTRO, {skipReset: true});
        this._showPlayerBubble('So many blocks!', 2600);
        this._speak('VO-03', null, {
            onend: () => {
                this._setPhase(PHASES.PHASE_2_LOOP, {skipReset: true});
                this._showPlayerBubble('A new block!', 2800);
                this._speak('VO-04');
                this._stateMachine.transition('BUILDING');
                this._persistProgress();
            }
        });
    }

    _advanceToPhaseThreeUnlock () {
        this._setPhase(PHASES.PHASE_3_UNLOCK, {skipReset: true});
        this._showPlayerBubble('Real dance!', 2600);
        this._speak('VO-08', null, {
            onend: () => {
                this._advanceToPhaseThreeBuild();
            }
        });
    }

    _advanceToPhaseThreeBuild () {
        this._setPhase(PHASES.PHASE_3_BUILD, {skipReset: true});
        this._showPlayerBubble('Even more!', 2600);
        this._speak('VO-09');
        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _advanceToCelebration () {
        this._setPhase(PHASES.CELEBRATION, {skipReset: true});
        this._outcomeFlags.outcome_6 = true;
        this._launchCelebration();
        this._showPlayerBubble('Moonwalk!', 3200);
        this._speak('VO-15', null, {
            onend: () => {
                this._clearPhaseTimeout();
                this._advanceToBonus();
            }
        });
        this._clearPhaseTimeout();
        this._phaseTimeout = setTimeout(() => {
            this._advanceToBonus();
        }, 9000);
        this._stateMachine.transition('BUILDING');
    }

    _advanceToBonus () {
        if (this._phase === PHASES.BONUS || this._phase === PHASES.COMPLETE) {
            return;
        }
        this._setPhase(PHASES.BONUS, {skipReset: true});
        this._showDoneButton();
        this._showPlayerBubble('Free play!', 3000);
        this._speak('VO-16');
        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _completeBonus () {
        this._completionSaved = true;
        this._setPhase(PHASES.COMPLETE, {skipReset: true});
        this._showPlayerBubble('Loop master!', 3200);
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        this._persistProgress({
            status: 'completed',
            completedAt: new Date().toISOString()
        });
        // Show our own completion modal — do NOT transition to SUCCESS
        // (SUCCESS triggers the old FeedbackModal in ActivityUI)
        this._showCompletionModal();
    }

    _showCompletionModal () {
        this._completionOverlay = document.createElement('div');
        this._completionOverlay.className = 'activity-completion-overlay';

        const modal = document.createElement('div');
        modal.className = 'activity-completion-modal';

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'activity-completion-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
        });
        modal.appendChild(closeBtn);

        // Header with Blooks branding
        const header = document.createElement('div');
        header.className = 'activity-completion-header';
        const logo = document.createElement('img');
        logo.src = 'assets/lobby/scratchJrlogo.svg';
        logo.alt = 'Blooks';
        logo.className = 'activity-completion-logo';
        header.appendChild(logo);
        const title = document.createElement('h3');
        title.className = 'activity-completion-title';
        title.textContent = 'Activity Complete!';
        header.appendChild(title);
        const subtitle = document.createElement('p');
        subtitle.className = 'activity-completion-subtitle';
        subtitle.textContent = 'Moonwalk Machine';
        header.appendChild(subtitle);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'activity-completion-body';

        const stars = document.createElement('div');
        stars.className = 'activity-completion-stars';
        stars.textContent = '\u2B50\u2B50\u2B50';
        body.appendChild(stars);

        const message = document.createElement('p');
        message.className = 'activity-completion-message';
        message.textContent = "You are a loop master! You taught Blooks to dance using a loop that repeats blocks over and over.";
        body.appendChild(message);

        const actions = document.createElement('div');
        actions.className = 'activity-completion-actions';

        const replayBtn = document.createElement('button');
        replayBtn.className = 'activity-completion-btn-replay';
        replayBtn.setAttribute('aria-label', 'Replay activity');
        replayBtn.innerHTML = '&#8634; Replay';
        replayBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            this.restartActivity();
        });
        actions.appendChild(replayBtn);

        const nextBtn = document.createElement('button');
        nextBtn.className = 'activity-completion-btn-next';
        nextBtn.setAttribute('aria-label', 'Next activity');
        nextBtn.innerHTML = 'Next Activity &#9654;';
        nextBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            ScratchJr.saveProject(null, ScratchJr.flippage);
        });
        actions.appendChild(nextBtn);

        body.appendChild(actions);
        modal.appendChild(body);
        this._completionOverlay.appendChild(modal);
        document.body.appendChild(this._completionOverlay);
    }

    _dismissCompletionModal () {
        if (this._completionOverlay) {
            this._completionOverlay.style.opacity = '0';
            setTimeout(() => {
                if (this._completionOverlay && this._completionOverlay.parentNode) {
                    this._completionOverlay.parentNode.removeChild(this._completionOverlay);
                }
                this._completionOverlay = null;
            }, 300);
        }
    }

    _buildPhasePrompt (phase, isResume = false) {
        const prefix = isResume ? "You're back. " : '';
        if (phase === PHASES.PHASE_3_BUILD) {
            if ((this._analysis.insideDistinctCount || 0) < 3) {
                return `${prefix}Add three different kinds of blocks inside the loop and test the dance again.`;
            }
            return `${prefix}Run the loop a few more times and watch your full moonwalk dance.`;
        }
        if (phase === PHASES.PHASE_2_LOOP || phase === PHASES.LOOP_INTRO) {
            if (!this._analysis.hasRepeatWithInner) {
                return `${prefix}Put a blue move block inside the orange block.`;
            }
            if (!this._repeatParameterChanged) {
                return `${prefix}Tap the number on the orange block and run it again.`;
            }
            if (!this._multiActionLoop) {
                return `${prefix}Add one more kind of block inside the loop.`;
            }
            if (this._analysis.hasOutsideLoopContent) {
                return `${prefix}Drag the extra blocks inside the orange block.`;
            }
            return `${prefix}Try another run and see what changed.`;
        }
        if (phase === PHASES.BONUS) {
            return 'You can keep experimenting now. Try the say block or make the dance go forever.';
        }
        if (phase === PHASES.COMPLETE) {
            return `${prefix}You finished this moonwalk. Tap Done or restart to play again.`;
        }
        if (!this._analysis.hasOnflag) {
            return `${prefix}Start with the green flag.`;
        }
        return `${prefix}Add a few blue move blocks to make Blooks walk.`;
    }

    _buildPhaseBubble (phase, isResume = false) {
        if (phase === PHASES.PHASE_3_BUILD) {
            return (this._analysis.insideDistinctCount || 0) < 3
                ? 'Add more moves!'
                : 'Run it again!';
        }
        if (phase === PHASES.PHASE_2_LOOP || phase === PHASES.LOOP_INTRO) {
            if (!this._analysis.hasRepeatWithInner) {
                return 'Put one inside!';
            }
            if (!this._repeatParameterChanged) {
                return 'Change the number!';
            }
            if (!this._multiActionLoop) {
                return 'Add one more block!';
            }
            if (this._analysis.hasOutsideLoopContent) {
                return 'Move blocks inside!';
            }
            return isResume ? 'You were looping!' : 'Keep looping!';
        }
        if (phase === PHASES.BONUS) {
            return 'Free play!';
        }
        if (phase === PHASES.COMPLETE) {
            return 'You finished!';
        }
        return this._analysis.hasOnflag ? 'Add move blocks!' : 'Start with green flag!';
    }

    _startIdleScaffolding () {
        this._clearIdleTimers();
        if (this._phase === PHASES.INTRO || this._phase === PHASES.CELEBRATION || this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delayMs, index) => {
            const timer = setTimeout(() => {
                this._handleIdleTier(index + 1);
            }, delayMs);
            this._idleTimers.push(timer);
        });
    }

    _handleIdleTier(tier) {
        if (this._stateMachine && this._stateMachine.state === 'RUNNING') {
            return;
        }
        if (Date.now() - this._lastInteractionAt < 800) {
            return;
        }

        if (tier === 1) {
            this._speak('VO-10', this._buildPhasePrompt(this._phase));
            return;
        }

        if (tier === 2) {
            this._highlightHintTarget();
            return;
        }

        if (tier === 3) {
            const hint = this._buildSpecificHint();
            this._highlightHintTarget(true);
            this._speak(hint.voiceId, hint.text);
            return;
        }

        this._showGhostHand();
    }

    _buildSpecificHint () {
        if (this._phase === PHASES.PHASE_1_STACK) {
            if (!this._analysis.hasOnflag) {
                return {voiceId: 'VO-02', text: 'Start with the green flag first.'};
            }
            return {voiceId: 'VO-03', text: 'Add a few blue move blocks to build a longer walk.'};
        }

        if (!this._analysis.hasRepeatWithInner) {
            return {voiceId: 'VO-04', text: 'Put your blocks inside the orange block.'};
        }

        if (this._analysis.hasOutsideLoopContent) {
            return {voiceId: 'VO-18', text: 'Drag the extra blocks inside the orange block.'};
        }

        if (!this._repeatParameterChanged) {
            return {voiceId: 'VO-12', text: 'Tap the number on the repeat block and change it.'};
        }

        if (this._phase === PHASES.PHASE_3_BUILD) {
            return {voiceId: 'VO-10', text: 'What else could you add to the dance?'};
        }

        return {voiceId: 'VO-07', text: 'Add another kind of block inside the loop.'};
    }

    _highlightHintTarget(prominent = false) {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (!target) {
            return;
        }

        if (target.kind === 'run') {
            const runButton = this._findRunButton();
            if (runButton) {
                runButton.classList.add(prominent ? 'activity-storywalk-target' : 'activity-storywalk-pulse');
            }
            return;
        }

        if (target.kind === 'script-block') {
            const scriptBlock = this._findScriptBlockElement(target.blockType);
            if (scriptBlock) {
                scriptBlock.classList.add(prominent ? 'activity-storywalk-target' : 'activity-storywalk-pulse');
            }
            return;
        }

        this._selectPaletteCategory(target.categoryIndex);
        const block = this._findPaletteBlock(target.blockType);
        if (block) {
            block.classList.add(prominent ? 'activity-storywalk-target' : 'activity-storywalk-pulse');
        }
    }

    _showGhostHand () {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (!target || target.kind === 'run') {
            this._highlightHintTarget(true);
            return;
        }

        let block = null;
        if (target.kind === 'script-block') {
            block = this._findScriptBlockElement(target.blockType);
        } else {
            this._selectPaletteCategory(target.categoryIndex);
            block = this._findPaletteBlock(target.blockType);
        }
        const scripts = gn('scripts');
        if (!block || !scripts) {
            return;
        }

        const blockRect = block.getBoundingClientRect();
        const scriptRect = scripts.getBoundingClientRect();
        this._ghostHand = document.createElement('div');
        this._ghostHand.className = 'activity-storywalk-ghost-hand';
        this._ghostHand.style.left = `${blockRect.left + (blockRect.width / 2)}px`;
        this._ghostHand.style.top = `${blockRect.top + (blockRect.height / 2)}px`;
        this._ghostHand.style.setProperty('--activity-ghost-dx', `${scriptRect.left + 140 - (blockRect.left + (blockRect.width / 2))}px`);
        this._ghostHand.style.setProperty('--activity-ghost-dy', `${scriptRect.top + 58 - (blockRect.top + (blockRect.height / 2))}px`);
        document.body.appendChild(this._ghostHand);

        setTimeout(() => {
            if (this._ghostHand && this._ghostHand.parentNode) {
                this._ghostHand.parentNode.removeChild(this._ghostHand);
                this._ghostHand = null;
            }
        }, 1800);
    }

    _clearHintVisuals () {
        const root = (this._container && this._container.ownerDocument) || document;
        root.querySelectorAll('.activity-storywalk-pulse').forEach((element) => {
            element.classList.remove('activity-storywalk-pulse');
        });
        root.querySelectorAll('.activity-storywalk-target').forEach((element) => {
            element.classList.remove('activity-storywalk-target');
        });
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
            this._ghostHand = null;
        }
    }

    _getHintTarget () {
        if (this._phase === PHASES.PHASE_1_STACK && !this._analysis.hasOnflag) {
            return {kind: 'block', blockType: 'onflag', categoryIndex: 0};
        }

        if (this._phase === PHASES.PHASE_1_STACK) {
            return {kind: 'block', blockType: 'forward', categoryIndex: 1};
        }

        if (this._analysis.hasOutsideLoopContent) {
            return {kind: 'script-block', blockType: 'repeat'};
        }

        if (!this._analysis.hasRepeatWithInner) {
            return {kind: 'block', blockType: 'repeat', categoryIndex: 4};
        }

        if (!this._repeatParameterChanged) {
            return {kind: 'script-block', blockType: 'repeat'};
        }

        if (this._phase === PHASES.BONUS) {
            return {kind: 'block', blockType: this._analysis.hasForever ? 'say' : 'forever', categoryIndex: this._analysis.hasForever ? 2 : 5};
        }

        return {kind: 'block', blockType: 'hop', categoryIndex: 1};
    }

    _findScriptBlockElement (blockType) {
        const block = this._getPlayerScriptBlocks().find((scriptBlock) => scriptBlock && scriptBlock.blocktype === blockType);
        return block && block.div ? block.div : null;
    }

    _schedulePersistProgress (overrides = {}, delayMs = 250) {
        this._clearPersistTimer();
        this._persistTimer = setTimeout(() => {
            this._persistTimer = null;
            this._persistProgress(overrides);
        }, delayMs);
    }

    _handleOutsideLoopCoaching () {
        if (!this._analysis.hasRepeatWithInner || !this._analysis.hasOutsideLoopContent) {
            return;
        }
        if (this._lastAnalysis && this._lastAnalysis.hasOutsideLoopContent) {
            return;
        }
        this._showPlayerBubble('Outside!', 2200);
        this._speak('VO-18');
    }

    _maybeSpeakReflectionPrompt () {
        if (!this._lastMeaningfulChange) {
            return;
        }

        let voiceId = null;
        let bubble = null;
        if (this._lastMeaningfulChange.kind === 'repeat-parameter') {
            voiceId = 'VO-12';
            bubble = 'Different!';
        } else if (this._lastMeaningfulChange.kind === 'inside-added') {
            voiceId = 'VO-11';
            bubble = 'New move!';
        } else if (this._lastMeaningfulChange.kind === 'inside-removed') {
            voiceId = 'VO-19';
            bubble = 'Wait, shorter?';
        } else if (this._lastMeaningfulChange.kind === 'speed-parameter') {
            voiceId = 'VO-20';
            bubble = 'Zooom!';
        }

        this._lastMeaningfulChange = null;
        if (!voiceId) {
            return;
        }

        if (bubble) {
            this._showPlayerBubble(bubble, 2400);
        }
        this._reflectionPromptsFired++;
        this._speak(voiceId);
    }

    _scheduleLongDancePrompt () {
        this._clearLongDanceTimeout();
        this._longDanceTimeout = setTimeout(() => {
            if (this._stateMachine && this._stateMachine.state === 'RUNNING') {
                this._showPlayerBubble('So long!', 2400);
                this._speak('VO-22');
            }
        }, 30000);
    }

    _startForeverPromptTimer () {
        this._clearForeverTimeout();
        if (this._phase !== PHASES.BONUS || !this._analysis.hasForever || this._foreverPromptShown) {
            return;
        }
        this._foreverTimeout = setTimeout(() => {
            if (this._stateMachine && this._stateMachine.state === 'RUNNING') {
                this._foreverPromptShown = true;
                this._showPlayerBubble('Forever!', 2600);
                this._speak('VO-23');
            }
        }, 15000);
    }

    _startLoopPulse () {
        this._stopLoopPulse();
        const pulse = () => {
            const blocks = this._getPlayerScriptBlocks().filter((block) => block && block.blocktype === 'repeat');
            blocks.forEach((block) => {
                if (block.div) {
                    block.div.classList.add('activity-moonwalk-loop-pulse');
                    setTimeout(() => {
                        if (block.div) {
                            block.div.classList.remove('activity-moonwalk-loop-pulse');
                        }
                    }, 220);
                }
            });
        };

        pulse();
        this._loopPulseTimer = setInterval(pulse, 520);
    }

    _stopLoopPulse () {
        if (this._loopPulseTimer) {
            clearInterval(this._loopPulseTimer);
            this._loopPulseTimer = null;
        }

        this._getPlayerScriptBlocks().forEach((block) => {
            if (block && block.div) {
                block.div.classList.remove('activity-moonwalk-loop-pulse');
            }
        });
    }

    _launchCelebration () {
        ActivityAudio.play('confetti');
        if (!this._celebrationLayer) {
            return;
        }
        this._celebrationLayer.innerHTML = '';
        for (let index = 0; index < 7; index++) {
            const star = document.createElement('span');
            star.className = 'activity-moonwalk-shooting-star';
            star.style.animationDelay = `${index * 0.18}s`;
            star.style.top = `${38 + (index * 20)}px`;
            star.style.left = `${40 + ((index % 3) * 90)}px`;
            this._celebrationLayer.appendChild(star);
        }
        this._celebrationLayer.classList.add('is-active');
        setTimeout(() => {
            if (this._celebrationLayer) {
                this._celebrationLayer.classList.remove('is-active');
                this._celebrationLayer.innerHTML = '';
            }
        }, 4200);
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }

        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-storywalk-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._showPlayerBubble('Mission complete!', 3200);
            this._speak('VO-17', null, {
                onend: () => this._completeBonus()
            });
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _showPlayerBubble (text, durationMs) {
        const player = this._getPlayerSpriteOwner();
        if (!player || typeof player.openBalloon !== 'function') {
            return;
        }
        player.openBalloon(text);
        if (durationMs && durationMs > 0) {
            setTimeout(() => {
                if (player && typeof player.closeBalloon === 'function') {
                    player.closeBalloon();
                }
            }, durationMs);
        }
    }

    _replayCurrentNarration () {
        if (this._lastNarrationId && this._lastNarrationText) {
            this._speak(this._lastNarrationId, this._lastNarrationText);
        }
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }

        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 5000) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }

        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let finished = false;
            let pollTimer = null;
            let timeoutId = null;

            const cleanup = () => {
                if (pollTimer) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                }
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                if (typeof window.speechSynthesis.removeEventListener === 'function') {
                    window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
                }
            };

            const finish = () => {
                if (finished) {
                    return;
                }
                finished = true;
                cleanup();
                this._refreshPreferredVoice();
                resolve();
            };

            const handleVoicesChanged = () => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            };

            if (typeof window.speechSynthesis.addEventListener === 'function') {
                window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
            }

            pollTimer = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            }, 100);

            timeoutId = setTimeout(finish, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }

        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }

        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);

        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        return scorePreferredNarrationVoice(voice, window);
    }

    _speak (voiceId, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[voiceId];
        if (!text) {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        this._lastNarrationId = voiceId;
        this._lastNarrationText = text;
        this._lastInteractionAt = Date.now();

        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            const preferredVoice = this._getPreferredVoice();
            utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
            if (preferredVoice) {
                utterance.voice = preferredVoice;
            }
            utterance.rate = 0.88;
            utterance.pitch = 1.18;
            utterance.volume = 1;
            utterance.onend = () => {
                if (opts.onend) {
                    opts.onend();
                }
            };
            window.speechSynthesis.speak(utterance);
        } catch (_) {
            if (opts.onend) {
                opts.onend();
            }
        }
    }

    _persistProgress (overrides = {}) {
        this._clearPersistTimer();
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const status = overrides.status || (this._completionSaved ? 'completed' : 'in_progress');

        return ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: 0,
            stars: status === 'completed' ? 3 : 0,
            bestScore: status === 'completed' ? 3 : 0,
            hintsUsed: 0,
            timeSpentMs: 0,
            currentState: overrides.currentState || this._phase,
            scriptSnapshot: this._analysis.signature,
            totalActiveTimeSec: Math.round(elapsedMs / 1000),
            flagTapCount: this._flagTapCount,
            maxLoopDepth: this._maxLoopDepth,
            parameterChanges: this._parameterChanges,
            duplicateBlocksRemoved: this._duplicateBlocksRemoved,
            reflectionPromptsFired: this._reflectionPromptsFired,
            phase3RunCount: this._phase3RunCount,
            loopDiscovered: this._loopDiscovered,
            repeatParameterChanged: this._repeatParameterChanged,
            multiActionLoop: this._multiActionLoop,
            outcomeFlags: {...this._outcomeFlags},
            completedAt: overrides.completedAt,
            lastOpenedAt: new Date().toISOString(),
            sessionStartedAt: this._sessionStartedAt,
            idleTimeMs: this._idleTimeMs
        });
    }

    _getPlayerSpriteOwner () {
        const spriteIds = ScratchJr.stage && ScratchJr.stage.currentPage && ScratchJr.stage.currentPage.getSprites
            ? ScratchJr.stage.currentPage.getSprites()
            : [];
        const preferredId = spriteIds.find((spriteId) => {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            return sprite && sprite.role === 'player';
        });
        const spriteId = preferredId || spriteIds[this._config.playerIndex] || spriteIds[0];
        const spriteEl = spriteId ? gn(spriteId) : null;
        return spriteEl && spriteEl.owner ? spriteEl.owner : null;
    }

    _getPlayerSpriteElement () {
        const player = this._getPlayerSpriteOwner();
        return player ? player.div : null;
    }

    _getPlayerScriptBlocks () {
        const player = this._getPlayerSpriteOwner();
        const scriptsDiv = player ? gn(`${player.id}_scripts`) : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return [];
        }
        return scriptsDiv.owner.getBlocks() || [];
    }

    _clearPlayerScript () {
        const player = this._getPlayerSpriteOwner();
        const scriptsDiv = player ? gn(`${player.id}_scripts`) : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return;
        }
        scriptsDiv.owner.dragList = scriptsDiv.owner.getBlocks();
        scriptsDiv.owner.deleteBlocks();
        scriptsDiv.owner.dragList = [];
        this._analysis = analyzeMoonwalkScripts([]);
        this._lastAnalysis = this._analysis;
    }

    _applySpriteRoles () {
        const spriteIds = ScratchJr.stage && ScratchJr.stage.currentPage && ScratchJr.stage.currentPage.getSprites
            ? ScratchJr.stage.currentPage.getSprites()
            : [];
        const templatePage = this._activityData.projectTemplate &&
            Array.isArray(this._activityData.projectTemplate.pages)
            ? this._activityData.projectTemplate.pages[0]
            : null;
        const templateSprites = templatePage && Array.isArray(templatePage.sprites) ? templatePage.sprites : [];

        spriteIds.forEach((spriteId, index) => {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            const templateSprite = templateSprites[index] || null;
            if (!sprite) {
                return;
            }
            if (templateSprite && templateSprite.role) {
                sprite.role = templateSprite.role;
            } else if (index === this._config.playerIndex) {
                sprite.role = 'player';
            } else if (index === this._config.goalIndex) {
                sprite.role = 'goal';
            }
        });
    }

    _forcePlayerSelection () {
        const player = this._getPlayerSpriteOwner();
        const page = ScratchJr.stage && ScratchJr.stage.currentPage ? ScratchJr.stage.currentPage : null;
        if (!player || !page) {
            return false;
        }

        page.currentSpriteName = player.id;
        page.lastSprite = player.id;
        page.setCurrentSprite(player);
        if (Thumbs && typeof Thumbs.updateSprites === 'function') {
            Thumbs.updateSprites();
        }
        this._pruneSpriteRail(player.id);
        return true;
    }

    _pruneSpriteRail (playerId) {
        const rail = document.getElementById('spritecc');
        if (!rail || !playerId) {
            return;
        }

        Array.from(rail.childNodes || []).forEach((thumb) => {
            if (!thumb || !thumb.owner) {
                return;
            }
            const ownerId = thumb.owner && thumb.owner.id ? thumb.owner.id : thumb.id;
            thumb.style.display = ownerId === playerId ? '' : 'none';
        });
    }

    _selectPaletteCategory (index) {
        if (typeof index !== 'number') {
            return;
        }
        try {
            Palette.selectCategory(index);
        } catch (_) {}
    }

    _findPaletteBlock (blockType) {
        const palette = document.getElementById('palette');
        if (!palette) {
            return null;
        }
        return Array.from(palette.childNodes || []).find((child) => child && child.owner && child.owner.blocktype === blockType) || null;
    }

    _findRunButton () {
        return document.getElementById('go');
    }

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
        if (!this._idlePausedAt) {
            this._idlePausedAt = Date.now();
        }
    }

    _clearPersistTimer () {
        if (this._persistTimer) {
            clearTimeout(this._persistTimer);
            this._persistTimer = null;
        }
    }

    _clearPhaseTimeout () {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
    }

    _clearLongDanceTimeout () {
        if (this._longDanceTimeout) {
            clearTimeout(this._longDanceTimeout);
            this._longDanceTimeout = null;
        }
    }

    _clearForeverTimeout () {
        if (this._foreverTimeout) {
            clearTimeout(this._foreverTimeout);
            this._foreverTimeout = null;
        }
    }
}

export {PHASES};
